import { cn, formatMoney } from '@bixo/ui'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { ReactNode } from 'react'
import './charts.css'
import { compactInr } from './format'

/* Chart conventions (dataviz skill): fixed categorical order, one axis, thin
 * marks with rounded data-ends, recessive grid, hover tooltip on every mark,
 * a legend only for 2+ series, text in ink colours (never the series colour). */

const SERIES = ['var(--viz-series-1)', 'var(--viz-series-2)', 'var(--viz-series-3)'] as const
const ORDINAL = [
  'var(--viz-ord-1)',
  'var(--viz-ord-2)',
  'var(--viz-ord-3)',
  'var(--viz-ord-4)',
  'var(--viz-ord-5)',
]

const num = (v: unknown): number => (typeof v === 'number' ? v : Number(v ?? 0))

type Datum = Record<string, string | number | null | undefined>

interface TooltipRow {
  name?: string | number
  value?: unknown
  color?: string
  dataKey?: string | number
}

function ChartTooltip({
  active,
  payload,
  label,
  money,
}: {
  active?: boolean
  payload?: TooltipRow[]
  label?: ReactNode
  money: boolean
}) {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-xl border border-border bg-surface/95 px-3 py-2 text-xs shadow-elevated backdrop-blur">
      {label !== undefined && <div className="mb-1 font-medium text-foreground">{label}</div>}
      <div className="grid gap-1">
        {payload.map((row) => (
          <div key={String(row.dataKey)} className="flex items-center justify-between gap-6">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span className="size-2 rounded-full" style={{ background: row.color }} aria-hidden />
              {row.name}
            </span>
            <span className="tabular font-medium text-foreground">
              {money ? formatMoney(String(row.value ?? 0)) : String(row.value ?? '')}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

const axisProps = {
  stroke: 'var(--viz-axis)',
  tick: { fill: 'var(--viz-axis)', fontSize: 11 },
  tickLine: false,
  axisLine: false,
} as const

/**
 * Ranked categories (top items, customers, partners…): horizontal bars, long
 * labels readable on the category axis.
 */
export function RankedBars({
  data,
  label,
  series,
  money = true,
  height,
  className,
}: {
  data: Datum[]
  label: string
  series: { key: string; label: string }[]
  money?: boolean
  height?: number
  className?: string
}) {
  const rows = data.map((d) => ({
    ...d,
    __label: String(d[label] ?? '—'),
    ...Object.fromEntries(series.map((s) => [s.key, num(d[s.key])])),
  }))
  const h = height ?? Math.max(160, rows.length * (series.length > 1 ? 44 : 32) + 40)

  return (
    <div className={cn('viz-root w-full', className)} style={{ height: h }}>
      <ResponsiveContainer>
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
          barGap={2}
          barCategoryGap="22%"
        >
          <CartesianGrid horizontal={false} stroke="var(--viz-grid)" />
          <XAxis
            type="number"
            {...axisProps}
            tickFormatter={(v: number) => (money ? compactInr(v) : String(v))}
          />
          <YAxis
            type="category"
            dataKey="__label"
            {...axisProps}
            width={140}
            tickFormatter={(v: string) => (v.length > 22 ? `${v.slice(0, 21)}…` : v)}
          />
          <Tooltip cursor={{ fill: 'var(--viz-cursor)' }} content={<ChartTooltip money={money} />} />
          {series.length > 1 && (
            <Legend
              verticalAlign="top"
              align="right"
              height={28}
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12 }}
            />
          )}
          {series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={SERIES[i % SERIES.length]}
              radius={[0, 4, 4, 0]}
              maxBarSize={18}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

/** Change over time: one or two lines, crosshair tooltip. */
export function TrendLines({
  data,
  x,
  series,
  money = true,
  height = 240,
  className,
}: {
  data: Datum[]
  x: string
  series: { key: string; label: string; muted?: boolean; dashed?: boolean }[]
  money?: boolean
  height?: number
  className?: string
}) {
  const rows = data.map((d) => ({ ...d, ...Object.fromEntries(series.map((s) => [s.key, num(d[s.key])])) }))

  return (
    <div className={cn('viz-root w-full', className)} style={{ height }}>
      <ResponsiveContainer>
        <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
          <XAxis dataKey={x} {...axisProps} minTickGap={24} />
          <YAxis
            {...axisProps}
            width={56}
            tickFormatter={(v: number) => (money ? compactInr(v) : String(v))}
          />
          <Tooltip
            cursor={{ stroke: 'var(--viz-axis)', strokeDasharray: '3 3' }}
            content={<ChartTooltip money={money} />}
          />
          {series.length > 1 && (
            <Legend
              verticalAlign="top"
              align="right"
              height={28}
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12 }}
            />
          )}
          {series.map((s, i) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.muted ? 'var(--viz-previous)' : SERIES[i % SERIES.length]}
              strokeWidth={2}
              strokeDasharray={s.dashed ? '4 4' : undefined}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--color-surface, #fff)' }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

/**
 * Part-to-whole for a few ordered buckets (ageing): ordinal ramp, with the
 * values listed beside it so identity never rests on colour alone.
 */
export function OrdinalDonut({
  data,
  center,
  className,
}: {
  data: { key: string; label: string; value: number }[]
  center?: ReactNode
  className?: string
}) {
  const total = data.reduce((sum, d) => sum + d.value, 0)

  return (
    <div className={cn('viz-root flex flex-col items-center gap-5 sm:flex-row', className)}>
      <div className="relative size-40 shrink-0">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={total > 0 ? data : [{ key: 'empty', label: 'Nothing due', value: 1 }]}
              dataKey="value"
              nameKey="label"
              innerRadius="64%"
              outerRadius="100%"
              paddingAngle={total > 0 ? 2 : 0}
              stroke="none"
              isAnimationActive={false}
            >
              {(total > 0 ? data : [{ key: 'empty' }]).map((d, i) => (
                <Cell key={d.key} fill={total > 0 ? ORDINAL[i % ORDINAL.length] : 'var(--viz-grid)'} />
              ))}
            </Pie>
            {total > 0 && <Tooltip content={<ChartTooltip money />} />}
          </PieChart>
        </ResponsiveContainer>
        {center && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
            {center}
          </div>
        )}
      </div>
      <ul className="grid w-full gap-1.5 text-sm">
        {data.map((d, i) => (
          <li key={d.key} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-2 text-muted-foreground">
              <span
                className="size-2.5 rounded-sm"
                style={{ background: ORDINAL[i % ORDINAL.length] }}
                aria-hidden
              />
              {d.label}
            </span>
            <span className="tabular font-medium">{formatMoney(String(d.value))}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Share of a whole across a handful of categories (stock valuation). */
export function ShareDonut({ data, label, value }: { data: Datum[]; label: string; value: string }) {
  const top = data.slice(0, 3)
  const rest = data.slice(3).reduce((sum, d) => sum + num(d[value]), 0)
  const slices = [
    ...top.map((d, i) => ({ key: `s${i}`, label: String(d[label] ?? '—'), value: num(d[value]) })),
    ...(rest > 0 ? [{ key: 'other', label: 'Other', value: rest }] : []),
  ]
  const colors = [...SERIES, 'var(--viz-previous)']

  return (
    <div className="viz-root flex flex-col items-center gap-5 sm:flex-row">
      <div className="size-40 shrink-0">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius="64%"
              outerRadius="100%"
              paddingAngle={2}
              stroke="none"
              isAnimationActive={false}
            >
              {slices.map((s, i) => (
                <Cell key={s.key} fill={colors[i] ?? 'var(--viz-previous)'} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip money />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="grid w-full gap-1.5 text-sm">
        {slices.map((s, i) => (
          <li key={s.key} className="flex items-center justify-between gap-4">
            <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
              <span className="size-2.5 shrink-0 rounded-sm" style={{ background: colors[i] }} aria-hidden />
              <span className="truncate">{s.label}</span>
            </span>
            <span className="tabular font-medium">{formatMoney(String(s.value))}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
